// @givmo/app-admin — tolerant decode of the employer SSO configuration surface (AS-WEB6).
//
// ══ WHY EVERYTHING HERE IS HAND-DECODED OFF `unknown` ══════════════════════════════════════════
// AS-BE17 landed the whole `/admin/company/settings/sso` router before the committed OpenAPI
// snapshot carried any of it, so these shapes were decoded by hand: `agent_docs/api-client.md`
// § *A whole ROUTE the snapshot lacks* is the sanctioned disposition — deliberately NOT "refresh
// the snapshot for one feature", which would absorb every unrelated backend change since the last
// regen. Same reasoning, same shape as AS-WEB4's `security-decode.ts` beside it. The snapshot has
// since caught up with ten of the router's twelve routes (the 2026-09-01 refresh, `a33e05d7`,
// generated their operations and types); the two it still lacks are AS-WEB25's
// `GET …/sso/connection` and AS-WEB28's `POST …/sso/connection/start`, whose answers are both
// read by {@link readSsoConnection}.
//
// ══ THE ONE RULE THAT DECIDES EVERY BRANCH BELOW ══════════════════════════════════════════════
// ⚠ AN UNREADABLE VALUE NEVER RESOLVES TOWARDS THE PERMISSIVE ANSWER. Every boolean is tested
// `=== true`; a string, a number or an object arriving on one is a shape this build does not
// understand, and reading it as "yes" would be guessing in the direction that says a domain is
// verified, a connection exists, or a tier may be raised.
//
// Two places that rule has teeth beyond tidiness, because both would otherwise DOWNGRADE a
// company's security posture from a decode accident:
//
//   1. AN UNRECOGNISED `sso_mode` DECODES TO `null`, NOT TO `none`. This screen submits a
//      WHOLE-STATE PUT. If a tier this build cannot name rendered as `none` selected, an Owner
//      pressing Save would send `none` and turn off a control they never touched. `null` means the
//      selector shows nothing selected and the save is refused by the caller — see
//      {@link canSubmitTier}.
//   2. `available_sso_modes` IS FILTERED TO WHAT THIS BUILD KNOWS, and an empty result is
//      "unknown", not "nothing is available". The screen renders no selector in that state rather
//      than offering the one tier it could still spell (`none`), which is again the downgrade.
//
// ══ COUNTS DECODE TRI-STATE, AND "NOT REPORTED" MUST ALSO BLOCK ═══════════════════════════════
// Every `lock-readiness` count is `number | null`, and `null` renders as NOT REPORTED rather than
// as `0`. These numbers are read immediately before an Owner decides whether to lock their whole
// workforce out of personal sign-in; a zero manufactured from an unreadable field would say "this
// affects nobody" on evidence that never arrived. `agent_docs/api-client.md` states the general
// form of this rule (an unreachable signal that NARROWS what a response claims must decode to "not
// reported"); a mis-decoded count here is the same failure with a worse consequence.
//
// ⚠ TOLERANCE ALONE IS NOT THE WHOLE GATE. Decoding a `{}` body into all-`null` is correct and is
// also, on its own, dangerous: the object is perfectly defined, so a caller gating on "did the read
// return something" would let an Owner confirm the flip having been shown no numbers at all. That is
// why {@link countsComplete} exists beside the reader — the tolerant decode says what arrived, and
// the completeness predicate says whether it was enough to decide on.

// ── The tier ────────────────────────────────────────────────────────────────────────────────────

/**
 * The employer's SSO tier — `CompanySsoMode`, whose wire values are the lowercase member values
 * (`app/domain/value_objects.py`). L0/L3/L4 of the auth-policy ladder; the MFA tiers are a separate
 * column and a separate control (`security-decode.ts`).
 */
import { dayCountLabel, hourCountLabel } from './security-copy';

export type SsoTier = 'none' | 'employer_context' | 'signin_lock';

/** The tiers in ladder order. Rendering follows this, never the server's array order. */
export const SSO_TIERS: readonly SsoTier[] = ['none', 'employer_context', 'signin_lock'];

/** `MIN`/`MAX_SSO_FRESHNESS_HOURS` — the endpoint's validated range (`ge=24`, `le=168`).
 *
 *  ⚠ Note the asymmetry with `MIN_MFA_GRACE_DAYS = 0`, which the backend documents and which is not
 *  an oversight to "fix" here: a zero grace window is a legitimate employer choice ("effective
 *  immediately") because it withholds a BENEFIT, while a zero freshness window would make every
 *  request stale the instant it was served — a company-wide denial of its own content driven by one
 *  integer, which is not a policy anyone can mean. */
export const MIN_SSO_FRESHNESS_HOURS = 24;
export const MAX_SSO_FRESHNESS_HOURS = 24 * 7;

/** `DEFAULT_SSO_FRESHNESS_HOURS` — what the BACKEND would use for a row carrying nothing. Matched
 *  deliberately rather than defaulted to a neutral-looking number, so this screen and the server
 *  agree about an unset column. */
export const DEFAULT_SSO_FRESHNESS_HOURS = 24;

/** The windows the picker offers. The endpoint accepts the whole range above, so a value already
 *  stored outside this set is preserved and shown rather than silently snapped to a neighbour. */
export const FRESHNESS_PRESETS: readonly number[] = [24, 48, 72, 168];

function isTier(value: unknown): value is SsoTier {
  return (SSO_TIERS as readonly unknown[]).includes(value);
}

function record(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

/** `true` only for a literal `true`. See the header — every boolean on this surface reads this way. */
function readBool(source: unknown, key: string): boolean {
  return record(source)[key] === true;
}

/** A non-empty string, or `null`. Whitespace-only is `null`: it is not a value anyone can act on. */
function readString(source: unknown, key: string): string | null {
  const value = record(source)[key];
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** A non-negative integer count, or `null` for "not reported". Never coerces towards zero. */
function readCount(source: unknown, key: string): number | null {
  const value = record(source)[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const floored = Math.floor(value);
  return floored >= 0 ? floored : null;
}

/** A tri-state boolean — `null` when the key is absent or unreadable, so the UI can say nothing. */
function readTriBool(source: unknown, key: string): boolean | null {
  const value = record(source)[key];
  return typeof value === 'boolean' ? value : null;
}

// ── `DomainVerificationOut` ─────────────────────────────────────────────────────────────────────

export interface DomainVerificationState {
  /** The company's domain as the backend normalised it, or `null` when it holds none we can use. */
  domain: string | null;
  verified: boolean;
  verifiedAt: string | null;
  /** `_givmo-verification.<domain>` — composed server-side, never here. */
  recordName: string | null;
  token: string | null;
  /** The server's own next-step guidance. Rendered VERBATIM; this module never paraphrases it. */
  instructions: string | null;
  /** Declared on the wire model but never populated by the backend today — read anyway, tri-state,
   *  so it renders the day it starts arriving instead of being silently dropped. */
  checkMessage: string | null;
}

export function readDomainVerification(source: unknown): DomainVerificationState {
  const block = record(source).domain_verification ?? source;
  return {
    domain: readString(block, 'domain'),
    verified: readBool(block, 'verified'),
    verifiedAt: readString(block, 'verified_at'),
    recordName: readString(block, 'record_name'),
    token: readString(block, 'token'),
    instructions: readString(block, 'instructions'),
    checkMessage: readString(block, 'check_message'),
  };
}

/**
 * Which of the stepper's states the domain block is in.
 *
 * ORDER MATTERS and is most-specific-first: `verified` wins over everything (a verified domain that
 * still carries a token is the ordinary post-check shape, since the token is not cleared on
 * success), and `unusable` is checked before `idle` so a company holding an email address or a URL
 * in `company.domain` gets the fix named rather than a Start button whose every press 400s.
 */
export type DomainStep = 'verified' | 'awaiting-record' | 'idle' | 'unusable';

export function domainStep(state: DomainVerificationState): DomainStep {
  if (state.verified) return 'verified';
  if (state.domain === null) return 'unusable';
  if (state.token !== null && state.recordName !== null) return 'awaiting-record';
  return 'idle';
}

// ── `SsoConnectionOut` ──────────────────────────────────────────────────────────────────────────

export interface SsoConnectionState {
  configured: boolean;
  connectionRef: string | null;
  /** The two values the employer's IT admin pastes into their OWN identity provider. */
  relyingPartyId: string | null;
  relyingPartyCallback: string | null;
  /** The server's NameID setup instruction. Rendered verbatim — see the copy module's rule 1. */
  setupGuidance: string | null;
}

export function readSsoConnection(source: unknown): SsoConnectionState {
  const block = record(source).connection ?? source;
  return {
    configured: readBool(block, 'configured'),
    connectionRef: readString(block, 'connection_ref'),
    relyingPartyId: readString(block, 'relying_party_id'),
    relyingPartyCallback: readString(block, 'relying_party_callback'),
    setupGuidance: readString(block, 'setup_guidance'),
  };
}

// ── `SsoConfigOut` ──────────────────────────────────────────────────────────────────────────────

export interface SsoConfigState {
  domain: DomainVerificationState;
  connection: SsoConnectionState;
  /** `null` when the wire carries a tier this build cannot name — see the header, rule 1. */
  mode: SsoTier | null;
  freshnessHours: number;
  /** Filtered to what this build knows. EMPTY means "could not read", not "nothing is served". */
  availableModes: SsoTier[];
  canEnableSso: boolean;
  /** AS-BE18b's directory-sync block. On the READ its broker-side fields are all `null`. */
  scim: ScimState;
}

export function readSsoConfig(source: unknown): SsoConfigState {
  const body = record(source);
  const raw = body.available_sso_modes;
  const availableModes = Array.isArray(raw) ? SSO_TIERS.filter((tier) => raw.includes(tier)) : [];
  return {
    domain: readDomainVerification(body),
    connection: readSsoConnection(body),
    mode: isTier(body.sso_mode) ? body.sso_mode : null,
    freshnessHours: clampFreshnessHours(body.sso_freshness_hours),
    availableModes,
    canEnableSso: readBool(body, 'can_enable_sso'),
    scim: readScim(body.scim),
  };
}

// ── `SsoScimOut` — directory sync (AS-BE18b / AS-WEB7) ──────────────────────────────────────────

/**
 * The state of the employer's SCIM directory sync.
 *
 * ★ `available` AND `enabled` ARE TWO DIFFERENT FACTS AND MUST NOT BE COLLAPSED. `available` is a
 * property of the DEPLOYMENT (has this environment armed `EMPLOYER_SCIM_ENABLED`); `enabled` is a
 * property of the EMPLOYER (has their Owner turned the sync on). They need opposite UI — a surface
 * that should not exist at all, versus a switch the Owner can flip — so a reader that treated either
 * `false` as "off" would render a directory-sync control in an environment whose every write 404s.
 * The backend states the same rule on `SsoScimOut`; this is the client half of it.
 *
 * ⚠ THREE OTHER FIELDS ARE TRI-STATE, AND `null` MEANS "NOT READ" — NEVER "false". The
 * configuration read makes no broker call on purpose (it is the tab's dark-reveal probe, so a broker
 * outage must not take the whole section down), so `directoryConfigured`, `scimBaseUrl` and
 * `hasBearerToken` arrive `null` there and carry real values only on a WRITE response. Collapsing
 * `null` to `false` would have the screen state "there is no directory" on evidence it never
 * received — which on the disable path is exactly backwards, since the directory certainly still
 * exists. `available` and `enabled` are the two the read always knows.
 *
 * ★ `bearerToken` IS SHOW-ONCE AND THIS IS ITS ONLY CARRIER. The broker keeps a hash, so the value
 * exists in exactly one place — the response that minted it. It is `null` on every read, on a second
 * enable, and on every subsequent render. Nothing may write it anywhere that outlives the response:
 * not the query cache, not storage, not a log. See `sso-scim-panel.tsx`.
 */
export interface ScimState {
  /**
   * Whether this DEPLOYMENT serves the SCIM surface at all.
   *
   * ⚠ READ `=== true`, so a response predating the field — or one this build cannot parse — hides
   * the surface rather than revealing it. That is the safe direction twice over: the writes would
   * 404 anyway, and it is what lets this client land BEFORE the backend field exists in an
   * environment. Never inferred from `enabled`.
   */
  available: boolean;
  /**
   * The stored switch — `company.scim_enabled`, never a network read.
   *
   * Meaningful only while {@link available} is true: a `false` here alongside `available: false`
   * says nothing about what the employer would choose, because they were never offered the choice.
   */
  enabled: boolean;
  /** `null` = not read. `true`/`false` only from a write response. */
  directoryConfigured: boolean | null;
  /** The URL the employer pastes into their identity provider. `null` on the read. */
  scimBaseUrl: string | null;
  /** Whether a token has ever been minted. `null` on the read. A `true` with no `bearerToken` is
   *  the "already set up, and we cannot show you the old value" state — there is nothing to reveal. */
  hasBearerToken: boolean | null;
  /** SHOW-ONCE. Present ONLY on an enable that minted, or a rotation. */
  bearerToken: string | null;
  /** The server's own next-step guidance, matched to the state. Rendered VERBATIM. */
  setupGuidance: string | null;
}

export function readScim(source: unknown): ScimState {
  return {
    available: readBool(source, 'available'),
    enabled: readBool(source, 'enabled'),
    directoryConfigured: readTriBool(source, 'directory_configured'),
    scimBaseUrl: readString(source, 'scim_base_url'),
    hasBearerToken: readTriBool(source, 'has_bearer_token'),
    bearerToken: readString(source, 'bearer_token'),
    setupGuidance: readString(source, 'setup_guidance'),
  };
}

/**
 * Whether this response is the ONE MOMENT a token value exists.
 *
 * The predicate the reveal panel opens on, and it is deliberately narrower than "the write
 * succeeded": an enable against an already-provisioned directory answers 200 with
 * `has_bearer_token: true` and NO token, and treating that as a mint would open an empty panel
 * telling an Owner to copy a value that is not there.
 */
export function mintedToken(scim: ScimState): string | null {
  return scim.bearerToken;
}

/**
 * Whether a directory is known NOT to exist.
 *
 * ⚠ ASKED IN THE NEGATIVE ON PURPOSE, because only a write ever answers it. `null` (the read) means
 * "we did not look", and the rotate affordance stays available in that state: rotation is the remedy
 * for a token an Owner believes is compromised, and withholding it until they first ARM a sweep that
 * deletes employment rows would refuse a security act on the grounds of an unrelated preference.
 * When there genuinely is no directory the backend answers a typed `SSO_SCIM_NOT_CONFIGURED` whose
 * sentence names the remedy, which is a better answer than a hidden control.
 */
export function directoryKnownAbsent(scim: ScimState): boolean {
  return scim.directoryConfigured === false;
}

/**
 * Whether the base URL may be shown.
 *
 * Only a write carries it, so this is `false` for the whole of an ordinary page load — the panel
 * says nothing rather than rendering an empty row or a placeholder that reads like a value.
 */
export function canShowScimBaseUrl(scim: ScimState): boolean {
  return scim.scimBaseUrl !== null;
}

/**
 * Coerce a stored freshness value into the endpoint's range.
 *
 * ⚠ `typeof x === 'number'` is not enough — `NaN` and `Infinity` are numbers, and both would survive
 * a naive clamp into the request body and 422. Non-integers are floored rather than rejected so a
 * value the backend somehow holds still renders as something an Owner can see and correct.
 */
export function clampFreshnessHours(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_SSO_FRESHNESS_HOURS;
  const floored = Math.floor(value);
  return Math.min(MAX_SSO_FRESHNESS_HOURS, Math.max(MIN_SSO_FRESHNESS_HOURS, floored));
}

/**
 * The tiers the selector renders: what this environment serves, PLUS whatever is currently stored.
 *
 * The union is not politeness. `available_sso_modes` is what may be WRITTEN; a stored tier can
 * legitimately sit outside it (an environment whose `EMPLOYER_SSO_SIGNIN_LOCK_ENABLED` was turned
 * back off still holds `signin_lock` on the row — the backend refuses new writes of it and clears
 * nothing). Omitting the stored tier would render a selector with nothing selected and invite an
 * Owner to "fix" it by choosing a lower one.
 *
 * Returned in ladder order, never in the server's array order.
 */
export function selectableTiers(config: SsoConfigState): SsoTier[] {
  const stored = config.mode;
  return SSO_TIERS.filter(
    (tier) => config.availableModes.includes(tier) || (stored !== null && tier === stored),
  );
}

/** Whether a tier may be SUBMITTED. A stored-but-no-longer-served tier renders selected and is not
 *  writable — the backend would refuse it `SSO_TIER_NOT_AVAILABLE`, so the control says so first. */
export function isTierWritable(config: SsoConfigState, tier: SsoTier): boolean {
  return config.availableModes.includes(tier);
}

/**
 * Whether the tier form may submit at all.
 *
 * FALSE on an unrecognised stored tier (header rule 1: a whole-state PUT would downgrade it) and
 * FALSE when the served-modes list could not be read (rule 2). Both are "this build cannot state
 * the current policy", and a form that cannot state the current policy must not restate it.
 */
export function canSubmitTier(config: SsoConfigState): boolean {
  return config.mode !== null && config.availableModes.length > 0;
}

/**
 * Whether a save CLAMPED — the tier that came back is not the tier that was sent.
 *
 * The backend clamps a tier above `none` to `none` when the domain is unverified or no connection
 * exists, and REPORTS it by returning the full config rather than failing. Detecting it here is
 * what turns that report into something the Owner reads; a silent accept would be the same
 * dishonesty as a silent clamp.
 */
export function tierWasClamped(requested: SsoTier, saved: SsoConfigState): boolean {
  return saved.mode !== null && saved.mode !== requested;
}

// ── `SsoTestConnectionStartOut` / `SsoTestConnectionOut` ────────────────────────────────────────

export interface TestConnectionStart {
  testId: string | null;
  authorizeUrl: string | null;
  state: string | null;
  setupGuidance: string | null;
}

export function readTestConnectionStart(source: unknown): TestConnectionStart {
  return {
    testId: readString(source, 'test_id'),
    authorizeUrl: readString(source, 'authorize_url'),
    state: readString(source, 'state'),
    setupGuidance: readString(source, 'setup_guidance'),
  };
}

/**
 * The five outcomes, kept separate because each has a different fix (plan §4).
 *
 * ⚠ `expired` IS NOT A TIMEOUT MESSAGE. An assertion the broker rejects at its own endpoint — a
 * non-email NameID (the Microsoft Entra pairwise-identifier case) or an email outside the verified
 * domain — is refused before any redirect back to us, so the browser never returns and the flow
 * simply expires. That expiry is the ONLY signal that case produces, which is why the backend's
 * `guidance` for it carries the NameID setup instruction and why this screen renders it as a
 * configuration instruction rather than as a failed sign-in.
 */
export type TestConnectionStatus =
  | 'pending'
  | 'ok'
  | 'subject_not_email'
  | 'domain_mismatch'
  | 'expired'
  // AS-BE92: read at once from the broker's own record of the login.
  | 'idp_refused'
  | 'idp_response_rejected';

const TEST_STATUSES: readonly TestConnectionStatus[] = [
  'pending',
  'ok',
  'subject_not_email',
  'domain_mismatch',
  'expired',
  'idp_refused',
  'idp_response_rejected',
];

/**
 * AS-BE92 — what the identity provider said when it refused the test.
 *
 * ⚠ UNSIGNED TEXT FROM THE IDENTITY PROVIDER. An identity provider signs assertions and a refusal
 * carries none, so anyone holding the test's broker id could have written this. It is shown as the
 * identity provider's words, as plain text, and never trusted: nothing here decides anything.
 */
export interface TestIdpRefusal {
  /** The top-level SAML StatusCode (`urn:oasis:names:tc:SAML:2.0:status:…`). */
  statusCode: string | null;
  subStatusCode: string | null;
  /** Capped at `IDP_MESSAGE_DISPLAY_LIMIT` characters, ending in an ellipsis when cut. */
  message: string | null;
}

/** The most of an identity provider's own words this screen shows. The broker and the backend bound
 *  them too; this holds the screen to it whatever arrives. */
export const IDP_MESSAGE_DISPLAY_LIMIT = 1024;

export interface TestConnectionResult {
  testId: string | null;
  /** `null` for a status this build does not recognise — which is never treated as success. */
  status: TestConnectionStatus | null;
  subject: string | null;
  subjectIsEmail: boolean | null;
  subjectDomainVerified: boolean | null;
  /** The server's own admin-facing explanation. Rendered verbatim, always. */
  guidance: string | null;
  /** AS-BE92 — on `idp_refused`, what the identity provider said; `null` otherwise. */
  idpRefusal: TestIdpRefusal | null;
  /** AS-BE92 — on `idp_response_rejected`, the broker's kind of refusal. The server's guidance names
   *  it in words, so nothing renders this; it is kept for whoever reads the result next. */
  rejectionKind: string | null;
}

export function readTestConnection(source: unknown): TestConnectionResult {
  const raw = record(source).status;
  return {
    testId: readString(source, 'test_id'),
    status:
      typeof raw === 'string' && (TEST_STATUSES as readonly string[]).includes(raw)
        ? (raw as TestConnectionStatus)
        : null,
    subject: readString(source, 'subject'),
    subjectIsEmail: readTriBool(source, 'subject_is_email'),
    subjectDomainVerified: readTriBool(source, 'subject_domain_verified'),
    guidance: readString(source, 'guidance'),
    idpRefusal: readIdpRefusal(record(source).idp_refusal),
    rejectionKind: readString(source, 'rejection_kind'),
  };
}

function readIdpRefusal(source: unknown): TestIdpRefusal | null {
  if (typeof source !== 'object' || source === null) return null;
  const message = readString(source, 'message');
  return {
    statusCode: readString(source, 'status_code'),
    subStatusCode: readString(source, 'sub_status_code'),
    message: message === null ? null : capIdpText(message),
  };
}

/** Cut by code point, so a cut never splits a character in two. */
function capIdpText(text: string): string {
  const chars = Array.from(text);
  return chars.length > IDP_MESSAGE_DISPLAY_LIMIT
    ? `${chars.slice(0, IDP_MESSAGE_DISPLAY_LIMIT - 1).join('')}…`
    : text;
}

const SAML_STATUS_PREFIX = 'urn:oasis:names:tc:SAML:2.0:status:';

/**
 * The refusal's status as a line an IT admin can read: a SAML core code by its own name
 * (`…:status:Requester` reads `Requester`), anything else as sent, the nested code after the top one.
 * `null` when the identity provider sent neither.
 */
export function idpStatusLine(refusal: TestIdpRefusal): string | null {
  const names = [refusal.statusCode, refusal.subStatusCode]
    .filter((code): code is string => code !== null)
    .map((code) =>
      code.startsWith(SAML_STATUS_PREFIX) ? code.slice(SAML_STATUS_PREFIX.length) : code,
    );
  return names.length > 0 ? names.join(' · ') : null;
}

/**
 * How the outcome is PRESENTED — three presentations over five (plus one unknown) statuses.
 *
 * ★ `unknown` presents as `action-needed`, never as `waiting` and never as `passed`. A status this
 * build cannot name is not evidence the test succeeded, and presenting it as "waiting" would leave
 * the screen polling forever against a terminal state.
 */
export type TestPresentation = 'waiting' | 'passed' | 'action-needed';

export function testPresentation(status: TestConnectionStatus | null): TestPresentation {
  if (status === 'pending') return 'waiting';
  if (status === 'ok') return 'passed';
  return 'action-needed';
}

/** Whether the poller should keep asking. Only an explicit `pending` is still in flight — an
 *  unreadable status stops the poll rather than spinning against a terminal answer. */
export function testIsPending(status: TestConnectionStatus | null): boolean {
  return status === 'pending';
}

// ── `SsoLockReadinessOut` ───────────────────────────────────────────────────────────────────────

/**
 * The L4 pre-flight counts. COUNTS, NEVER A ROSTER — the backend deliberately returns no names, and
 * this screen has nothing to add to that.
 *
 * Every count is `number | null` and `null` means NOT REPORTED. See the header for why a
 * manufactured zero is the specific failure this shape exists to prevent.
 */
export interface LockReadiness {
  linkedEmployees: number | null;
  wouldBeLocked: number | null;
  exemptAdmins: number | null;
  withoutSsoIdentity: number | null;
  belowClientFloor: number | null;
  clientVersionUnknown: number | null;
  /** `null` = NO FLOOR IS CONFIGURED HERE, so the two version counts above did not measure
   *  anything. The screen must say that rather than let a zero read as "everyone passed". */
  minClientVersion: string | null;
}

export function readLockReadiness(source: unknown): LockReadiness {
  return {
    linkedEmployees: readCount(source, 'linked_employees'),
    wouldBeLocked: readCount(source, 'would_be_locked'),
    exemptAdmins: readCount(source, 'exempt_admins'),
    withoutSsoIdentity: readCount(source, 'without_sso_identity'),
    belowClientFloor: readCount(source, 'below_client_floor'),
    clientVersionUnknown: readCount(source, 'client_version_unknown'),
    minClientVersion: readString(source, 'min_client_version'),
  };
}

/**
 * Whether NONE of this company's linked employees is also an admin of it.
 *
 * ⚠ THIS IS INFORMATIONAL, NOT A WARNING, and reading it the other way is the mistake worth naming.
 * `exempt_admins` is computed by walking `company._employees` and counting the ones whose
 * `admin_company` is this company — so it counts admins who are ALSO LINKED EMPLOYEES, and nothing
 * else. An admin who is not a linked employee (the usual Owner) is never in that roster: they never
 * receive the lock claim, are skipped by the passkey refusal, and keep every personal sign-in. A
 * zero therefore does NOT mean "nobody can turn this off" — the Owner reading the screen almost
 * certainly can. It means only that the exemption applies to none of the employees.
 *
 * Only an explicit, readable `0` returns true. `null` (not reported) does NOT — asserting anything
 * from an absent count is the same error as manufacturing a zero, pointed the other way.
 */
export function hasNoExemptAdmin(readiness: LockReadiness): boolean {
  return readiness.exemptAdmins === 0;
}

/**
 * Whether the pre-flight read actually delivered the NUMBERS, not merely a 200.
 *
 * ⚠ THE GATE THIS EXISTS FOR IS "the Owner has SEEN the counts", and `data !== undefined` does not
 * express it: {@link readLockReadiness} is a tolerant reader, so a `{}` body — a shape drift, a
 * non-JSON 200 from a proxy or captive portal, a field rename — decodes to an all-`null` object that
 * is perfectly defined. The ceremony would then render no count rows at all and still enable
 * Confirm, which is precisely the "never saw the numbers" case the exit bar forbids.
 *
 * All six counts are load-bearing, so all six are required. `min_client_version` is NOT: `null`
 * there is a real, documented state (no floor configured in this environment) that the panel
 * already reports in words.
 */
export function countsComplete(readiness: LockReadiness): boolean {
  return (
    readiness.linkedEmployees !== null &&
    readiness.wouldBeLocked !== null &&
    readiness.exemptAdmins !== null &&
    readiness.withoutSsoIdentity !== null &&
    readiness.belowClientFloor !== null &&
    readiness.clientVersionUnknown !== null
  );
}

/** Whether the app-version counts measured anything at all (`min_client_version` was configured). */
export function clientFloorWasChecked(readiness: LockReadiness): boolean {
  return readiness.minClientVersion !== null;
}

// ── The typed confirmation ──────────────────────────────────────────────────────────────────────

/**
 * Whether what the Owner typed matches the company name closely enough to confirm the L4 flip.
 *
 * EXACT ON THE NAME, TOLERANT ON THE TYPING: whitespace is trimmed and collapsed and the comparison
 * is case-insensitive. That is deliberate rather than lax — the control exists to make the act
 * DELIBERATE, and requiring byte-exact capitalisation of a name like "Acme, Inc." mostly teaches
 * people to paste it from the heading above, which defeats the purpose it was added for. No near
 * miss passes: a different name, a partial name or an empty box all fail.
 *
 * An empty or unusable company name can never be confirmed — otherwise an empty box would satisfy
 * an empty expectation and the ceremony's last gate would open itself.
 */
export function confirmationMatches(typed: string, companyName: string): boolean {
  const normalize = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();
  const expected = normalize(companyName);
  if (expected.length === 0) return false;
  return normalize(typed) === expected;
}

// ── Display helpers ─────────────────────────────────────────────────────────────────────────────

/** "March 4, 2026", or `null` for an unparseable stamp — never the epoch, never "Invalid Date". */
export function formatVerifiedAt(iso: string | null): string | null {
  if (iso === null) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/** "24 hours" / "7 days" — the picker's option labels. Whole days read as days. */
export function formatFreshnessHours(hours: number): string {
  if (hours % 24 === 0) return dayCountLabel(hours / 24);
  return hourCountLabel(hours);
}

/**
 * The freshness options: the presets, plus a stored value already outside that set.
 *
 * Same disposition as `graceOptions` in the MFA section — silently moving a stored window to the
 * nearest preset the moment an Owner opens the screen would change a policy they did not touch.
 */
export function freshnessOptions(current: number): { value: string; label: string }[] {
  return [...new Set([...FRESHNESS_PRESETS, current])]
    .filter((hours) => hours >= MIN_SSO_FRESHNESS_HOURS && hours <= MAX_SSO_FRESHNESS_HOURS)
    .sort((a, b) => a - b)
    .map((hours) => ({ value: String(hours), label: formatFreshnessHours(hours) }));
}
